import React, { useState, useEffect, useCallback } from "react";
import api from "../../services/api";
import {
  PageContainer,
  PageHeader,
  Card,
  CardHeader,
  StatCard,
  Table,
  Thead,
  Th,
  Td,
  TRow,
  EmptyState,
  Button,
  FormGroup,
  Input,
  TabGroup,
  Badge,
  AlertBanner,
  Divider,
} from "../ui";

// ============================================================
// TYPES
// ============================================================

interface GiftCard {
  id: string;
  code: string;
  balance: number;
  initial_value: number;
  recipient_name: string;
  recipient_email: string;
  recipient_phone: string;
  is_physical: boolean;
  status: "active" | "empty" | "voided" | "expired";
  issued_at: string;
}

interface GiftCardTransaction {
  id: string;
  type: "activation" | "reload" | "redemption" | "void" | "reissue";
  amount: number;
  balance_after: number;
  description: string;
  created_at: string;
}

interface LiabilityData {
  total_active_cards: number;
  total_liability: number;
  activated_this_month: number;
}

// ============================================================
// HELPERS
// ============================================================

function formatDate(d: string): string {
  if (!d) return "--";
  return new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

function formatCurrency(n: number | string | null | undefined): string {
  // Postgres numeric columns (lifetime_spend, balances) arrive as strings;
  // "12.50".toFixed is not a function and took the whole app down (10/6).
  return "$" + (Number(n) || 0).toFixed(2);
}

function maskCode(code: string): string {
  if (!code || code.length <= 4) return code || "";
  return "\u2022\u2022\u2022\u2022 " + code.slice(-4);
}

function statusBadgeVariant(status: string): "green" | "default" | "danger" | "gold" {
  switch (status) {
    case "active": return "green";
    case "empty": return "default";
    case "voided": return "danger";
    case "expired": return "gold";
    default: return "default";
  }
}

// ============================================================
// OVERVIEW TAB
// ============================================================

function OverviewTab() {
  const [cards, setCards] = useState<GiftCard[]>([]);
  const [liability, setLiability] = useState<LiabilityData>({
    total_active_cards: 0,
    total_liability: 0,
    activated_this_month: 0,
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get("/loyalty/gift-cards", { params: { status: "active" } }),
      api.get("/loyalty/gift-cards/liability"),
    ])
      .then(([cardsRes, liabilityRes]) => {
        setCards(cardsRes.data);
        setLiability(liabilityRes.data);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <EmptyState message="Loading..." icon="..." />;

  return (
    <div>
      <div className="grid grid-cols-3 gap-4 mb-6">
        <StatCard label="Active Cards" value={String(liability.total_active_cards)} variant="blue" />
        <StatCard label="Total Liability" value={formatCurrency(liability.total_liability)} variant="gold" />
        <StatCard label="Activated This Month" value={String(liability.activated_this_month)} variant="green" />
      </div>

      {cards.length === 0 ? (
        <EmptyState message="No active gift cards" detail="Activate a card to get started" />
      ) : (
        <Table>
          <Thead>
            <Th>Code</Th>
            <Th align="right">Balance</Th>
            <Th>Recipient</Th>
            <Th>Issued</Th>
            <Th>Type</Th>
            <Th>Status</Th>
          </Thead>
          <tbody>
            {cards.map((c, i) => (
              <TRow key={c.id} index={i}>
                <Td bold>{maskCode(c.code)}</Td>
                <Td align="right">{formatCurrency(c.balance)}</Td>
                <Td>{c.recipient_name || "--"}</Td>
                <Td>{formatDate(c.issued_at)}</Td>
                <Td>
                  <Badge variant={c.is_physical ? "default" : "blue"}>
                    {c.is_physical ? "Physical" : "Digital"}
                  </Badge>
                </Td>
                <Td>
                  <Badge variant={statusBadgeVariant(c.status)}>{c.status}</Badge>
                </Td>
              </TRow>
            ))}
          </tbody>
        </Table>
      )}
    </div>
  );
}

// ============================================================
// ACTIVATE TAB
// ============================================================

function ActivateTab() {
  const [form, setForm] = useState({
    value: "",
    recipient_name: "",
    recipient_email: "",
    recipient_phone: "",
    is_physical: false,
  });
  const [saving, setSaving] = useState(false);
  const [activatedCode, setActivatedCode] = useState<string | null>(null);

  const handleActivate = async () => {
    setSaving(true);
    setActivatedCode(null);
    try {
      const res = await api.post("/loyalty/gift-cards/activate", {
        value: Number(form.value),
        recipient_name: form.recipient_name,
        recipient_email: form.recipient_email,
        recipient_phone: form.recipient_phone,
        is_physical: form.is_physical,
      });
      setActivatedCode(res.data.code);
      setForm({ value: "", recipient_name: "", recipient_email: "", recipient_phone: "", is_physical: false });
    } catch {
      // ignore
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      {activatedCode && (
        <AlertBanner variant="green">
          Card activated! Code: <strong>{activatedCode}</strong>
        </AlertBanner>
      )}

      <Card>
        <CardHeader title="Activate New Gift Card" />
        <div className="grid grid-cols-2 gap-4">
          <FormGroup label="Card Value ($)">
            <Input
              value={form.value}
              onChange={(v) => setForm({ ...form, value: v })}
              type="number"
              min="1"
              step="0.01"
              placeholder="25.00"
              required
            />
          </FormGroup>
          <FormGroup label="Recipient Name">
            <Input
              value={form.recipient_name}
              onChange={(v) => setForm({ ...form, recipient_name: v })}
              placeholder="John Smith"
            />
          </FormGroup>
          <FormGroup label="Recipient Email">
            <Input
              value={form.recipient_email}
              onChange={(v) => setForm({ ...form, recipient_email: v })}
              type="email"
              placeholder="john@example.com"
            />
          </FormGroup>
          <FormGroup label="Recipient Phone">
            <Input
              value={form.recipient_phone}
              onChange={(v) => setForm({ ...form, recipient_phone: v })}
              placeholder="717-555-1234"
            />
          </FormGroup>
          <FormGroup label="Card Type">
            <div className="flex items-center gap-4 mt-2">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="card_type"
                  checked={!form.is_physical}
                  onChange={() => setForm({ ...form, is_physical: false })}
                  className="w-4 h-4"
                />
                <span className="text-sm" style={{ color: "var(--rbw-navy)" }}>Digital</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="card_type"
                  checked={form.is_physical}
                  onChange={() => setForm({ ...form, is_physical: true })}
                  className="w-4 h-4"
                />
                <span className="text-sm" style={{ color: "var(--rbw-navy)" }}>Physical</span>
              </label>
            </div>
          </FormGroup>
        </div>
        <div className="mt-6 flex justify-end">
          <Button onClick={handleActivate} disabled={saving || !form.value}>
            {saving ? "Activating..." : "Activate Card"}
          </Button>
        </div>
      </Card>
    </div>
  );
}

// ============================================================
// MANAGE TAB
// ============================================================

function ManageTab() {
  const [lookupCode, setLookupCode] = useState("");
  const [card, setCard] = useState<GiftCard | null>(null);
  const [transactions, setTransactions] = useState<GiftCardTransaction[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Action states
  const [reloadAmount, setReloadAmount] = useState("");
  const [voidReason, setVoidReason] = useState("");
  const [actionLoading, setActionLoading] = useState(false);
  const [actionMessage, setActionMessage] = useState("");

  const handleLookup = useCallback(async () => {
    if (!lookupCode.trim()) return;
    setLoading(true);
    setError("");
    setCard(null);
    setTransactions([]);
    setActionMessage("");
    try {
      const balRes = await api.get(`/loyalty/gift-cards/balance/${lookupCode}`);
      setCard(balRes.data);
      const txRes = await api.get(`/loyalty/gift-cards/${balRes.data.id}/transactions`);
      setTransactions(txRes.data);
    } catch {
      setError("Card not found. Check the code and try again.");
    } finally {
      setLoading(false);
    }
  }, [lookupCode]);

  const refreshCard = async () => {
    if (!card) return;
    try {
      const balRes = await api.get(`/loyalty/gift-cards/balance/${lookupCode}`);
      setCard(balRes.data);
      const txRes = await api.get(`/loyalty/gift-cards/${balRes.data.id}/transactions`);
      setTransactions(txRes.data);
    } catch {
      // ignore
    }
  };

  const handleReload = async () => {
    if (!reloadAmount) return;
    setActionLoading(true);
    try {
      await api.post("/loyalty/gift-cards/reload", { code: lookupCode, amount: Number(reloadAmount) });
      setActionMessage("Card reloaded successfully");
      setReloadAmount("");
      await refreshCard();
    } catch {
      setActionMessage("Reload failed");
    } finally {
      setActionLoading(false);
    }
  };

  const handleVoid = async () => {
    if (!voidReason) return;
    setActionLoading(true);
    try {
      await api.post("/loyalty/gift-cards/void", { code: lookupCode, reason: voidReason });
      setActionMessage("Card voided successfully");
      setVoidReason("");
      await refreshCard();
    } catch {
      setActionMessage("Void failed");
    } finally {
      setActionLoading(false);
    }
  };

  const handleReissue = async () => {
    setActionLoading(true);
    try {
      const res = await api.post("/loyalty/gift-cards/reissue", { old_code: lookupCode });
      setActionMessage(`Card reissued. New code: ${res.data.code}`);
      setLookupCode(res.data.code);
      await refreshCard();
    } catch {
      setActionMessage("Reissue failed");
    } finally {
      setActionLoading(false);
    }
  };

  return (
    <div>
      <div className="flex items-center gap-3 mb-6">
        <div className="flex-1">
          <Input
            value={lookupCode}
            onChange={setLookupCode}
            placeholder="Enter gift card code..."
          />
        </div>
        <Button onClick={handleLookup} variant="secondary" size="sm" disabled={loading}>
          {loading ? "Looking up..." : "Look Up"}
        </Button>
      </div>

      {error && <AlertBanner variant="danger">{error}</AlertBanner>}
      {actionMessage && <AlertBanner variant="blue">{actionMessage}</AlertBanner>}

      {card && (
        <>
          <Card className="mb-6">
            <CardHeader title="Card Details" />
            <div className="grid grid-cols-4 gap-4 mb-4">
              <div>
                <div className="text-xs font-semibold uppercase mb-1" style={{ color: "var(--rbw-gray-dark)" }}>Code</div>
                <div className="text-lg font-bold" style={{ color: "var(--rbw-navy)" }}>{card.code}</div>
              </div>
              <div>
                <div className="text-xs font-semibold uppercase mb-1" style={{ color: "var(--rbw-gray-dark)" }}>Balance</div>
                <div className="text-lg font-bold" style={{ color: "var(--rbw-navy)" }}>{formatCurrency(card.balance)}</div>
              </div>
              <div>
                <div className="text-xs font-semibold uppercase mb-1" style={{ color: "var(--rbw-gray-dark)" }}>Status</div>
                <Badge variant={statusBadgeVariant(card.status)}>{card.status}</Badge>
              </div>
              <div>
                <div className="text-xs font-semibold uppercase mb-1" style={{ color: "var(--rbw-gray-dark)" }}>Recipient</div>
                <div className="text-sm" style={{ color: "var(--rbw-navy)" }}>{card.recipient_name || "--"}</div>
              </div>
            </div>

            <Divider />

            <div className="text-sm font-semibold mb-3" style={{ color: "var(--rbw-navy)" }}>Actions</div>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <div className="flex items-center gap-2">
                  <Input value={reloadAmount} onChange={setReloadAmount} placeholder="Amount" type="number" min="0.01" step="0.01" />
                  <Button onClick={handleReload} size="sm" variant="secondary" disabled={actionLoading || !reloadAmount}>
                    Reload
                  </Button>
                </div>
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <Input value={voidReason} onChange={setVoidReason} placeholder="Reason" />
                  <Button onClick={handleVoid} size="sm" variant="danger" disabled={actionLoading || !voidReason}>
                    Void
                  </Button>
                </div>
              </div>
              <div>
                <Button onClick={handleReissue} size="sm" variant="gold" disabled={actionLoading}>
                  Reissue (Lost/Stolen)
                </Button>
              </div>
            </div>
          </Card>

          <Card>
            <CardHeader title="Transaction History" />
            {transactions.length === 0 ? (
              <EmptyState message="No transactions yet" />
            ) : (
              <Table>
                <Thead>
                  <Th>Date</Th>
                  <Th>Type</Th>
                  <Th align="right">Amount</Th>
                  <Th align="right">Balance After</Th>
                  <Th>Description</Th>
                </Thead>
                <tbody>
                  {transactions.map((t, i) => (
                    <TRow key={t.id} index={i}>
                      <Td>{formatDate(t.created_at)}</Td>
                      <Td>
                        <Badge variant={
                          t.type === "activation" ? "green"
                            : t.type === "reload" ? "blue"
                            : t.type === "redemption" ? "gold"
                            : t.type === "void" ? "danger"
                            : "default"
                        }>
                          {t.type}
                        </Badge>
                      </Td>
                      <Td align="right">{formatCurrency(t.amount)}</Td>
                      <Td align="right">{formatCurrency(t.balance_after)}</Td>
                      <Td>{t.description || "--"}</Td>
                    </TRow>
                  ))}
                </tbody>
              </Table>
            )}
          </Card>
        </>
      )}
    </div>
  );
}

// ============================================================
// BALANCE CHECK TAB
// ============================================================

function BalanceCheckTab() {
  const [code, setCode] = useState("");
  const [balance, setBalance] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleCheck = async () => {
    if (!code.trim()) return;
    setLoading(true);
    setError("");
    setBalance(null);
    try {
      const res = await api.get(`/loyalty/gift-cards/balance/${code}`);
      setBalance(res.data.balance);
    } catch {
      setError("Card not found. Please check the code and try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-md mx-auto mt-8">
      <Card>
        <div className="text-center mb-6">
          <h2 className="text-xl font-bold" style={{ color: "var(--rbw-navy)" }}>Check Gift Card Balance</h2>
          <p className="text-sm mt-1" style={{ color: "var(--rbw-gray-dark)" }}>
            Enter your gift card code below
          </p>
        </div>

        <div className="space-y-4">
          <Input
            value={code}
            onChange={setCode}
            placeholder="Enter gift card code"
          />
          <Button onClick={handleCheck} disabled={loading || !code.trim()} className="w-full">
            {loading ? "Checking..." : "Check Balance"}
          </Button>
        </div>

        {error && (
          <div className="mt-4">
            <AlertBanner variant="danger">{error}</AlertBanner>
          </div>
        )}

        {balance !== null && (
          <div className="mt-6 text-center p-6 rounded-xl" style={{ background: "var(--rbw-bg)" }}>
            <div className="text-sm font-semibold uppercase mb-1" style={{ color: "var(--rbw-gray-dark)" }}>
              Available Balance
            </div>
            <div className="text-4xl font-bold" style={{ color: "var(--rbw-navy)" }}>
              {formatCurrency(balance)}
            </div>
          </div>
        )}
      </Card>
    </div>
  );
}

// ============================================================
// MAIN GIFT CARD PAGE
// ============================================================

export default function GiftCardPage() {
  const [activeTab, setActiveTab] = useState("overview");

  const tabs = [
    { key: "overview", label: "Overview" },
    { key: "activate", label: "Activate" },
    { key: "manage", label: "Manage" },
    { key: "balance", label: "Balance Check" },
  ];

  return (
    <PageContainer>
      <PageHeader
        title="Gift Cards"
        subtitle="Activate, manage, and track gift card balances"
      />
      <TabGroup tabs={tabs} active={activeTab} onChange={setActiveTab} />

      {activeTab === "overview" && <OverviewTab />}
      {activeTab === "activate" && <ActivateTab />}
      {activeTab === "manage" && <ManageTab />}
      {activeTab === "balance" && <BalanceCheckTab />}
    </PageContainer>
  );
}
